import React, { useState, useEffect } from 'react';
import { useCompany } from '../../contexts/CompanyContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  LinearProgress
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { trackFormSubmission } from '../../utils/analytics';
import { Close as CloseIcon } from '@mui/icons-material';
import { ensureHttps, logUrl } from '../../utils/urlUtils';

export default function CompanyProfileForm({ skipPermissionCheck = false }) {
  const { companyProfile, updateCompanyProfile, loading, error } = useCompany();
  const { currentUser } = useAuth();
  const { canManageSettings, isLoading: permissionsLoading } = usePermissions();
  // Initialize form data - handle both 'name' and 'companyName' fields for compatibility
  const [formData, setFormData] = useState({
    companyName: companyProfile?.companyName || companyProfile?.name || '',
    address: companyProfile?.address || '',
    phone: companyProfile?.phone || '',
    email: companyProfile?.email || '',
    website: companyProfile?.website || '',
    licenseNumber: companyProfile?.licenseNumber || '',
    defaultTaxRate: companyProfile?.defaultTaxRate || 0,
    defaultTerms: companyProfile?.defaultTerms || '',
    logo: companyProfile?.logo ? ensureHttps(companyProfile.logo) : null
  });
  
  // Update form data when companyProfile changes
  useEffect(() => {
    if (companyProfile) {
      setFormData(prev => ({
        companyName: companyProfile.companyName || companyProfile.name || prev.companyName || '',
        address: companyProfile.address || prev.address || '',
        phone: companyProfile.phone || prev.phone || '',
        email: companyProfile.email || prev.email || '',
        website: companyProfile.website || prev.website || '',
        licenseNumber: companyProfile.licenseNumber || prev.licenseNumber || '',
        defaultTaxRate: companyProfile.defaultTaxRate ?? prev.defaultTaxRate ?? 0,
        defaultTerms: companyProfile.defaultTerms || prev.defaultTerms || '',
        logo: companyProfile.logo ? ensureHttps(companyProfile.logo) : prev.logo || null
      }));
      
      // Log logo URL when companyProfile changes
      if (companyProfile.logo) {
        logUrl('Company Profile Logo (from DB)', companyProfile.logo);
        const httpsLogo = ensureHttps(companyProfile.logo);
        if (httpsLogo !== companyProfile.logo) {
          console.warn('Company logo URL was HTTP, converted to HTTPS');
        }
      }
    }
  }, [companyProfile]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [success, setSuccess] = useState(false);

  const compressImage = async (file) => {
    try {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
          const img = new Image();
          img.src = event.target.result;
          img.onload = () => {
            const canvas = document.createElement('canvas');
            let width = img.width;
            let height = img.height;
            
            // Calculate new dimensions while maintaining aspect ratio
            const MAX_WIDTH = 800; // Reduced from 1200 to 800 for faster uploads
            const MAX_HEIGHT = 800;
            
            if (width > height) {
              if (width > MAX_WIDTH) {
                height = Math.round((height * MAX_WIDTH) / width);
                width = MAX_WIDTH;
              }
            } else {
              if (height > MAX_HEIGHT) {
                width = Math.round((width * MAX_HEIGHT) / height);
                height = MAX_HEIGHT;
              }
            }
            
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            
            // Convert to blob with quality 0.7 for better compression
            canvas.toBlob((blob) => {
              resolve(new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              }));
            }, 'image/jpeg', 0.7);
          };
        };
      });
    } catch (error) {
      console.error('Error compressing image:', error);
      throw new Error('Failed to compress image');
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif']
    },
    maxSize: 52428800, // 50MB
    onDrop: async (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        await handleLogoUpload(acceptedFiles[0]);
      }
    },
    onDropRejected: (fileRejections) => {
      const error = fileRejections[0].errors[0];
      if (error.code === 'file-too-large') {
        setUploadError('File is too large. Maximum size is 50MB.');
      } else if (error.code === 'file-invalid-type') {
        setUploadError('Invalid file type. Please upload a PNG, JPG, or GIF image.');
      } else {
        setUploadError('Error uploading file. Please try again.');
      }
    }
  });

  const handleLogoUpload = async (file) => {
    try {
      setUploading(true);
      setUploadError(null);
      setUploadProgress(0);
      
      console.log('Starting upload process...');
      console.log('Original file size:', file.size / 1024 / 1024, 'MB');
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        throw new Error('Invalid file type. Please upload an image.');
      }

      // Validate file size
      if (file.size > 52428800) { // 50MB
        throw new Error('File is too large. Maximum size is 50MB.');
      }

      console.log('Compressing image...');
      // Compress image before upload
      const compressedFile = await compressImage(file);
      console.log('Compressed file size:', compressedFile.size / 1024 / 1024, 'MB');
      
      if (!currentUser) {
        throw new Error('You must be logged in to upload images');
      }

      const storage = getStorage();
      const timestamp = Date.now();
      const logoRef = ref(storage, `company-logos/${currentUser.uid}/${timestamp}_${compressedFile.name}`);
      
      console.log('Uploading to Firebase...');
      try {
        // Upload the file
        await uploadBytes(logoRef, compressedFile);
        console.log('Upload complete, getting download URL...');
        
        // Get the download URL after successful upload
        const downloadURL = await getDownloadURL(logoRef);
        console.log('Download URL obtained:', downloadURL);
        
        // Ensure HTTPS
        const httpsUrl = ensureHttps(downloadURL);
        logUrl('Company Logo Upload', httpsUrl);
        
        setFormData(prev => ({ ...prev, logo: httpsUrl }));
        setUploading(false);
        setUploadProgress(0);
        console.log('Upload process completed successfully');
      } catch (uploadError) {
        console.error('Firebase upload error:', uploadError);
        throw new Error('Failed to upload to storage. Please try again.');
      }
    } catch (error) {
      console.error('Error in handleLogoUpload:', error);
      setUploadError(error.message || 'Failed to upload logo. Please try again.');
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleRemoveLogo = () => {
    setFormData(prev => ({ ...prev, logo: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateCompanyProfile(formData);
      setSuccess(true);
      trackFormSubmission('company_profile', true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error updating company profile:', error);
      trackFormSubmission('company_profile', false, error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    // Handle number fields
    const processedValue = type === 'number' ? parseFloat(value) || 0 : value;
    setFormData(prev => ({ ...prev, [name]: processedValue }));
  };

  // During onboarding (skipPermissionCheck), allow form to render even if company is still loading
  // The form will work with empty defaults and update when company data loads
  if (loading && !skipPermissionCheck) {
    return <CircularProgress />;
  }

  // Skip permission check if explicitly requested (e.g., during onboarding)
  // or if permissions are still loading (to prevent blocking during initial load)
  if (!skipPermissionCheck && !permissionsLoading && !canManageSettings) {
    return (
      <Paper elevation={3} sx={{ p: 3, maxWidth: 600, mx: 'auto', mt: 4 }}>
        <Alert severity="warning">
          You don't have permission to manage company settings. Only administrators and managers can edit the company profile.
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3, maxWidth: 600, mx: 'auto', mt: 4 }}>
      <Typography variant="h5" gutterBottom>
        Company Profile
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {uploadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {uploadError}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Company profile updated successfully!
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Box sx={{ mb: 3 }}>
          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'grey.300',
              borderRadius: 1,
              p: 3,
              textAlign: 'center',
              cursor: 'pointer',
              mb: 2,
              backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'action.hover'
              }
            }}
          >
            <input {...getInputProps()} />
            {formData.logo ? (
              <Box sx={{ position: 'relative' }}>
                <img 
                  src={ensureHttps(formData.logo)} 
                  alt="Company logo" 
                  onLoad={() => logUrl('Company Logo Image Load', formData.logo)}
                  onError={(e) => {
                    console.error('Error loading company logo:', formData.logo);
                    logUrl('Company Logo Image Error', formData.logo);
                  }}
                  style={{ 
                    maxHeight: 100, 
                    maxWidth: '100%',
                    objectFit: 'contain'
                  }} 
                />
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveLogo();
                  }}
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    backgroundColor: 'background.paper',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Click or drag to replace logo
                </Typography>
              </Box>
            ) : (
              <Box>
                <Typography>
                  {uploading ? (
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                        <CircularProgress size={20} />
                        Uploading... {Math.round(uploadProgress)}%
                      </Box>
                      <LinearProgress variant="determinate" value={uploadProgress} />
                    </Box>
                  ) : (
                    'Click or drag to upload company logo'
                  )}
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                  Supported formats: PNG, JPG, GIF (max 50MB)
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <TextField
          fullWidth
          label="Company Name"
          name="companyName"
          value={formData.companyName}
          onChange={handleChange}
          required
          margin="normal"
        />

        <TextField
          fullWidth
          label="Address"
          name="address"
          value={formData.address}
          onChange={handleChange}
          multiline
          rows={3}
          margin="normal"
        />

        <TextField
          fullWidth
          label="Phone"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          margin="normal"
        />

        <TextField
          fullWidth
          label="Email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          margin="normal"
        />

        <TextField
          fullWidth
          label="Website"
          name="website"
          value={formData.website}
          onChange={handleChange}
          margin="normal"
        />

        <TextField
          fullWidth
          label="License Number"
          name="licenseNumber"
          value={formData.licenseNumber}
          onChange={handleChange}
          margin="normal"
          helperText="Your contractor/business license number"
        />

        <TextField
          fullWidth
          label="Default Tax Rate (%)"
          name="defaultTaxRate"
          type="number"
          value={formData.defaultTaxRate || ''}
          onChange={handleChange}
          margin="normal"
          inputProps={{ min: 0, max: 100, step: 0.01 }}
          helperText="Default tax rate applied to estimates (e.g., 8.5 for 8.5%)"
        />

        <TextField
          fullWidth
          label="Default Estimate Footer / Terms"
          name="defaultTerms"
          value={formData.defaultTerms}
          onChange={handleChange}
          multiline
          rows={6}
          margin="normal"
          helperText="Default terms and conditions text displayed on all estimates (e.g., payment terms, warranty info, etc.)"
          placeholder="Example: Payment is due within 30 days of invoice date. A 1.5% monthly service charge will be applied to overdue accounts..."
        />

        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          size="large"
          sx={{ mt: 3 }}
          disabled={uploading}
        >
          {uploading ? 'Saving...' : 'Save Company Profile'}
        </Button>
      </form>
    </Paper>
  );
} 